import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { getAnnouncement } from "@/lib/api";
import HeaderSearch from "./HeaderSearch";
import { useLang } from "@/context/LangContext";

/**
 * Site header. Two rules per the brief:
 *  - No gradients, no glass, no glow. Plain plum on white.
 *  - The typographic wordmark ("Colorado Catholic Conference") IS the logo.
 *    A small mark at the left is set in gold on plum — the "seal" element.
 *  - No mobile "AI assistant" bubble anywhere; the assistant is opened
 *    only via named calls-to-action inside the page.
 */
export default function Header() {
  const [announcement, setAnnouncement] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { lang, setLang } = useLang();

  useEffect(() => {
    getAnnouncement().then(setAnnouncement).catch(() => {});
  }, []);

  const isES = lang === "es";

  const nav = isES
    ? [
        { to: "/about", label: "Acerca de" },
        { to: "/legislative-work", label: "Trabajo Legislativo" },
        { to: "/resources", label: "Recursos" },
        { to: "/take-action", label: "Actuar" },
        { to: "/contact", label: "Contacto" },
      ]
    : [
        { to: "/about", label: "About" },
        { to: "/legislative-work", label: "Legislative Work" },
        { to: "/resources", label: "Resources" },
        { to: "/take-action", label: "Take Action" },
        { to: "/contact", label: "Contact" },
      ];

  return (
    <header className="w-full" data-testid="site-header">
      {announcement?.active && announcement?.text && (
        <div
          className="bg-ccc-plum-deep text-ccc-paper"
          data-testid="announcement-bar"
          style={{ fontSize: "15px" }}
        >
          <div className="max-w-6xl mx-auto px-6 py-2.5 flex items-center justify-center gap-3 text-center">
            <span>{announcement.text}</span>
            {announcement.link_url && (
              <Link
                to={announcement.link_url}
                className="underline decoration-ccc-gold-light underline-offset-4 hover:text-ccc-paper transition-colors font-semibold"
                data-testid="announcement-link"
              >
                {announcement.link_text || (isES ? "Más" : "More")}
              </Link>
            )}
          </div>
        </div>
      )}

      <div className="bg-ccc-white border-b border-ccc-rule">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3" data-testid="site-logo">
            <div className="w-11 h-11 flex items-center justify-center bg-ccc-plum-deep">
              <svg
                viewBox="0 0 40 40"
                className="w-6 h-6"
                fill="none"
                stroke="#B08D3F"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M20 4 L20 36 M12 20 L28 20" strokeLinecap="round" />
              </svg>
            </div>
            <div className="leading-[1.05]">
              <div
                className="font-serif font-bold text-ccc-ink"
                style={{ fontSize: "22px", letterSpacing: "-0.01em" }}
              >
                Colorado Catholic
              </div>
              <div
                className="font-serif font-bold text-ccc-ink -mt-0.5"
                style={{ fontSize: "22px", letterSpacing: "-0.01em" }}
              >
                Conference
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-semibold transition-colors ${
                    isActive
                      ? "text-ccc-plum"
                      : "text-ccc-ink hover:text-ccc-plum"
                  }`
                }
                style={{ fontSize: "17px" }}
              >
                {n.label}
              </NavLink>
            ))}
            <HeaderSearch />
            <div
              className="flex items-center border border-ccc-rule-strong overflow-hidden"
              data-testid="lang-toggle"
              role="group"
              aria-label={isES ? "Idioma" : "Language"}
            >
              <button
                onClick={() => setLang("en")}
                className={`px-2.5 py-1 font-semibold transition-colors ${
                  lang === "en"
                    ? "bg-ccc-plum text-ccc-paper"
                    : "bg-ccc-white text-ccc-body hover:bg-ccc-paper"
                }`}
                style={{ fontSize: "14px" }}
                data-testid="lang-en-btn"
                aria-label="English"
                aria-pressed={lang === "en"}
              >
                EN
              </button>
              <button
                onClick={() => setLang("es")}
                className={`px-2.5 py-1 font-semibold border-l border-ccc-rule-strong transition-colors ${
                  lang === "es"
                    ? "bg-ccc-plum text-ccc-paper"
                    : "bg-ccc-white text-ccc-body hover:bg-ccc-paper"
                }`}
                style={{ fontSize: "14px" }}
                data-testid="lang-es-btn"
                aria-label="Español"
                aria-pressed={lang === "es"}
              >
                ES
              </button>
            </div>
            <Link
              to="/take-action/action-alerts"
              data-testid="header-take-action"
              className="bg-ccc-plum text-ccc-paper hover:bg-ccc-plum-deep px-5 py-2.5 font-semibold uppercase tracking-[0.08em] transition-colors"
              style={{ fontSize: "15px" }}
            >
              {isES ? "Actuar ahora" : "Take Action"}
            </Link>
          </nav>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden text-ccc-ink"
            data-testid="mobile-menu-toggle"
            aria-label={isES ? "Menú" : "Menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileOpen && (
          <div
            className="lg:hidden border-t border-ccc-rule bg-ccc-white"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setMobileOpen(false)}
                  className="text-ccc-ink py-3 border-b border-ccc-rule font-semibold"
                  style={{ fontSize: "17px" }}
                >
                  {n.label}
                </NavLink>
              ))}
              <div className="flex items-center gap-4 mt-4 mb-2">
                <button
                  onClick={() => setLang("en")}
                  className={`px-3 py-1 font-semibold border ${
                    lang === "en"
                      ? "bg-ccc-plum text-ccc-paper border-ccc-plum"
                      : "bg-ccc-white text-ccc-body border-ccc-rule"
                  }`}
                  style={{ fontSize: "14px" }}
                  aria-pressed={lang === "en"}
                >
                  EN
                </button>
                <button
                  onClick={() => setLang("es")}
                  className={`px-3 py-1 font-semibold border ${
                    lang === "es"
                      ? "bg-ccc-plum text-ccc-paper border-ccc-plum"
                      : "bg-ccc-white text-ccc-body border-ccc-rule"
                  }`}
                  style={{ fontSize: "14px" }}
                  aria-pressed={lang === "es"}
                >
                  ES
                </button>
              </div>
              <Link
                to="/take-action/action-alerts"
                onClick={() => setMobileOpen(false)}
                className="mt-2 bg-ccc-plum text-ccc-paper px-4 py-3 text-center font-semibold uppercase tracking-[0.08em]"
                style={{ fontSize: "15px" }}
              >
                {isES ? "Actuar ahora" : "Take Action"}
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
